<template>
  <div class="omni-assist-container">
    <div v-if="loading" class="loading-content">
      <div class="spinner"></div>
      <p>{{ loadingMessage }}</p>
    </div>
    <router-view v-else />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useWidgetStore } from '@/stores/widget'

const router = useRouter()
const store = useWidgetStore()

const loading = ref(true)
const loadingMessage = ref('Initializing...')

onMounted(async () => {
  try {
    // If we're on the /auth/callback route (OAuth PKCE return), let that view handle it
    if (window.location.pathname === '/auth/callback') {
      loading.value = false
      return
    }

    // 1. Extract embed token from URL.
    // No token → standalone mode: render LoginView, authenticate against the
    // platform auth-service with email + password, then enter the widget.
    // The embed-token path remains for iframe-embedded providers.
    const params = new URLSearchParams(window.location.search)
    const token = params.get('t')

    if (!token) {
      // Standalone mode: no embed token. Load the standalone provider stub
      // so LoginView can call setJwt() after authentication. Real
      // ProviderConfig (features, branding, provider-specific config) is
      // fetched from the connector via /widget/config AFTER login — see
      // LoginView.vue.
      const { loadProvider } = await import('@/providers/registry')
      const provider = await loadProvider('standalone')
      ;(window as any).__assistProvider = provider
      router.replace({ name: 'login' })
      loading.value = false
      return
    }

    store.setEmbedToken(token)
    ;(window as any).__adaptcx_embed_token = token

    // Save Genesys interaction widget params (survive OAuth redirect)
    const gcConversationId = params.get('conversationid') || params.get('conversationId') || ''
    const gcHostOrigin = params.get('gcHostOrigin') || ''
    const gcTargetEnv = params.get('gcTargetEnv') || ''
    if (gcConversationId) sessionStorage.setItem('adaptcx_gc_conversation_id', gcConversationId)
    if (gcHostOrigin) sessionStorage.setItem('adaptcx_gc_host_origin', gcHostOrigin)
    if (gcTargetEnv) sessionStorage.setItem('adaptcx_gc_target_env', gcTargetEnv)

    loadingMessage.value = 'Validating token...'

    // 2. Bootstrap — get provider config from backend
    const { bootstrapWidget } = await import('@/services/bootstrap')
    const config = await bootstrapWidget(token)
    store.setProviderConfig(config)

    // v2.3.0 (Phase 2.29): per-tenant chrome colors retired in favor
    // of the AdaptCX editorial palette. `branding.primary_color` and
    // `branding.gradient_end_color` are no longer applied to the
    // widget — chrome is fixed ink/paper/clay across all tenants.
    // Brand expression collapses to logo, brand_name, and (in the
    // login shell) tagline. See docs/migration/phase-2/phase-2.29.

    // Load widget translations (async, non-blocking)
    import('@/i18n/useLanguage').then(({ useLanguage }) => {
      useLanguage().loadLanguage()
    }).catch(() => { /* keep English */ })

    loadingMessage.value = 'Checking security...'

    // 3. Validate iframe embedding — skip for tab-based providers.
    // Standalone (AdaptCX login) and Google CCaaS (OAuth) launch as top-level
    // tabs, not iframes, so the iframe-origin check would always fail.
    // Backend token validation + per-provider auth remain authoritative.
    const TAB_BASED_PROVIDERS = ['standalone', 'google_ccaas']
    if (!TAB_BASED_PROVIDERS.includes(config.provider)) {
      const { validateEmbedding } = await import('@/services/security')
      const isValid = await validateEmbedding(config.allowed_origins)
      if (!isValid) {
        store.setError('This widget can only be loaded from an authorized domain.')
        router.replace({ name: 'error' })
        loading.value = false
        return
      }
    }

    loadingMessage.value = 'Loading provider...'

    // 4. Load provider adapter
    const { loadProvider } = await import('@/providers/registry')
    const provider = await loadProvider(config.provider)
    await provider.initialize(config)

    // Store provider on window for socket service access
    ;(window as any).__assistProvider = provider

    // 5. OAuth-based providers — redirect to login (PKCE flow)
    loadingMessage.value = 'Authenticating...'
    const authResult = await provider.authenticate()

    loadingMessage.value = 'Exchanging token...'
    const jwt = await provider.exchangeToken()
    store.setJwt(jwt)

    // Use auth result for agent info; if empty (e.g. Salesforce — fetched server-side),
    // fall back to provider.getAgentInfo() which is populated after exchangeToken()
    let agentId = authResult.agent_id
    let agentName = authResult.agent_name
    let agentEmail = authResult.agent_email
    if (!agentId && !agentName) {
      try {
        const info = await provider.getAgentInfo()
        agentId = info.id
        agentName = info.name
        agentEmail = info.email
      } catch { /* non-critical */ }
    }
    store.setAgent({ id: agentId, name: agentName, email: agentEmail })

    router.replace({ name: 'widget' })
  } catch (err: any) {
    console.error('Widget initialization failed:', err)
    store.setError(err.message || 'Failed to initialize widget.')
    router.replace({ name: 'error' })
  } finally {
    loading.value = false
  }
})
</script>
